import { ArrowLeftIcon } from "vega-icons";

import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { m } from "@vega-core/paraglide/messages";

import { useManageRolesModal } from "../../hooks/useManageRolesModal";

import { CollaboratorSelectionStep } from "./CollaboratorSelectionStep";
import { EditAssignmentsStep } from "./EditAssignmentsStep";

type ManageRolesModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const ManageRolesModal = ({ open, setOpen }: ManageRolesModalProps) => {
  const {
    isSelectStep,
    setStep,
    collaborators,
    selectedCollaborators,
    selection,
    manager,
    handleSave,
  } = useManageRolesModal({ open, setOpen });

  const titleNode = isSelectStep ? (
    m.settings_collaborators_manageRoles()
  ) : (
    <div className="flex items-center gap-2">
      <ThemedIconButton
        ariaLabel={m.btn_back()}
        dataTestId="manage-roles-back-button"
        disabled={manager.isSaving}
        icon={<ArrowLeftIcon className="w-4 h-4" />}
        onClick={() => setStep("select")}
        size="small"
        variant="noBackground"
      />
      <span>{m.settings_collaborators_manageRoles_editTitle()}</span>
    </div>
  );

  const description = isSelectStep
    ? m.settings_collaborators_manageRoles_description()
    : m.settings_collaborators_manageRoles_editDescription();

  const modalButtonsGroupProps = isSelectStep
    ? {
        secondaryAction: () => setOpen(false),
        secondaryActionText: m.btn_cancel(),
        mainAction: () => setStep("edit"),
        mainActionText: m.settings_collaborators_manageRoles_continue(),
        disabled: selection.selectedUserIds.size === 0,
      }
    : {
        secondaryAction: () => setOpen(false),
        secondaryActionText: m.btn_cancel(),
        disabledSecondary: manager.isSaving,
        mainAction: handleSave,
        mainActionText: m.common_saveChanges(),
        disabled:
          !manager.hasAnyChanges ||
          manager.hasAnyInvalidEntries ||
          manager.isSaving,
        isLoading: manager.isSaving,
      };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={modalButtonsGroupProps}
      dataTestId="manage-roles-modal"
      description={description}
      open={open}
      setOpen={setOpen}
      skipCloseGuards
      title={titleNode}
    >
      <div className="min-w-[36rem]">
        {isSelectStep ? (
          <CollaboratorSelectionStep
            collaborators={collaborators}
            selection={selection}
          />
        ) : (
          <EditAssignmentsStep
            collaborators={selectedCollaborators}
            manager={manager}
          />
        )}
      </div>
    </ActionaryModal>
  );
};
